import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Speed,
  Savings,
  TrendingUp,
  Verified,
  BusinessCenter,
  Gavel,
} from "@mui/icons-material";
import aboutImg from "../assets/aboutUs.jpg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Base page wrapper with smoke white background
const PageWrapper = styled(Box)({
  backgroundColor: "#F5F5F5",
  minHeight: "100vh",
});

// Styled Hero Section
const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(12, 0),
  background: `linear-gradient(135deg, #4BC8F2 30%, #115097 90%)`,
  color: theme.palette.common.white,
  textAlign: "center",
  overflow: "hidden",
}));

const KPISection = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(8),
  textAlign: "center",
}));

const StatCard = styled(Box)(({ theme }) => ({
  height: "180px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(4),
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius * 1.5,
  background: theme.palette.background.paper,
  transition: "transform 0.3s ease-in-out, boxShadow 0.3s ease-in-out",
  "&:hover": {
    transform: "scale(1.07)",
    boxShadow: theme.shadows[10],
  },
}));

const TeamCard = styled(Card)(({ theme }) => ({
  textAlign: "center",
  boxShadow: theme.shadows[6],
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: "#f5f5f5",
  transition: "transform 0.3s ease-in-out, boxShadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: theme.shadows[12],
  },
}));

export default function AboutUsPage() {
  const kpis = [
    { label: "Fast turnaround of project work", icon: Speed },
    { label: "Huge cost and time savings", icon: Savings },
    { label: "Significant improvement in productivity", icon: TrendingUp },
    { label: "Proven Reliability", icon: Verified },
    { label: "Business understanding and focus on ROI", icon: BusinessCenter },
    { label: "Honesty and Integrity", icon: Gavel },
  ];

  return (
    <PageWrapper>
      <HeroSection>
        <Container maxWidth="xl">
          <Typography variant="h2" fontWeight={700} gutterBottom>
            About Us
          </Typography>
          <Typography variant="h6" paragraph>
            Zenthra Solutions is a premier outsourcing company specialising in
            staffing and business solutions across various industries, including
            Business Process Outsourcing (BPO), IT & Software, Human Resources,
            Digital Marketing, and Healthcare. Our mission is to connect
            businesses with skilled professionals to enhance operations and
            workforce management.
          </Typography>
          <Box>
            <img
              style={{
                width: "40%",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
              }}
              src={aboutImg}
              alt="About Us"
            />
          </Box>
          <Typography variant="h6" paragraph>
            We focus on delivering expert IT solutions, managing HR functions,
            and providing staffing for healthcare, enabling organisations to
            optimise resources and achieve their goals. With a strong commitment
            to excellence and innovation, Zenthra Solutions helps businesses
            scale efficiently while concentrating on core objectives.
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ mt: 10 }}>
        <Typography variant="h4" fontWeight={600} align="center" gutterBottom>
          What can you expect?
        </Typography>
        <KPISection container spacing={4}>
          {kpis.map((kpi, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StatCard>
                <Avatar sx={{ bgcolor: "#115097", mb: 2 }}>
                  <kpi.icon />
                </Avatar>
                <Typography variant="h6" color="#115097" fontWeight={600}>
                  {kpi.label}
                </Typography>
              </StatCard>
            </Grid>
          ))}
        </KPISection>

        <Box sx={{ marginTop: 10, paddingBottom: 10 }}>
          <Typography variant="h4" fontWeight={600} align="center" gutterBottom>
            Our Mission & Values
          </Typography>
          <TeamCard>
            <CardContent>
              <Typography
                gutterBottom
                variant="h5"
                fontWeight={600}
                align="center"
              >
                Driving Innovation, Delivering Excellence
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Zenthra Solutions empowers businesses with seamless, efficient,
                and high-quality outsourcing solutions. We bridge the gap
                between talent and opportunity, allowing organisations to focus
                on growth while we handle recruitment, operations, and digital
                transformation. We specialise in{" "}
                <strong>
                  Business Process Outsourcing (BPO), IT & Software Outsourcing,
                  Human Resource Outsourcing (HRO), Digital Marketing & Content
                  Services, and Healthcare & Medical Outsourcing
                </strong>
                , delivering reliable, cost-effective, and scalable solutions.
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                align="center"
                gutterBottom
              >
                Our Values
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>Z</strong>eal –
                    Passionate about our mission.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>E</strong>xcellence –
                    Committed to the highest standards.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>N</strong>etworking –
                    Building strong, long-term partnerships.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>T</strong>ransparency –
                    Open and honest communication.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>H</strong>onor – Acting
                    with integrity and fairness.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>R</strong>espect –
                    Fostering an inclusive environment.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>
                    <strong style={{ fontSize: "19px" }}>A</strong>ccountability
                    – Taking responsibility for results.
                  </ListItemText>
                </ListItem>
              </List>
              <Typography variant="body1" color="text.secondary" align="center">
                Our values drive our commitment to innovation, excellence, and
                long-term success.
              </Typography>
            </CardContent>
          </TeamCard>
        </Box>
      </Container>
    </PageWrapper>
  );
}
